import { type JSX, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Spinner } from '@/components/ui';
import { setImpersonationToken } from '@/lib/auth';

/**
 * One-shot landing page that runs in the new tab opened by "View as".
 * Reads the access token from `window.location.hash`, stores it in this
 * tab's sessionStorage (so the reseller's localStorage isn't touched),
 * cleans the hash out of the URL bar, then navigates to /dashboard
 * which now sees the customer session.
 */
export default function ImpersonateHandoffPage(): JSX.Element {
  const navigate = useNavigate();
  useEffect(() => {
    const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
    const params = new URLSearchParams(raw);
    const token = params.get('token');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    setImpersonationToken(token);
    // Wipe the hash so the token isn't in the URL bar after the redirect.
    window.history.replaceState(null, '', window.location.pathname);
    navigate('/dashboard', { replace: true });
  }, [navigate]);

  return (
    <div className="grid place-items-center h-screen bg-paper">
      <div className="flex items-center gap-3 text-text-3 text-[14px]">
        <Spinner />
        <span>Switching session…</span>
      </div>
    </div>
  );
}
